import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { emptyView_softwareReleasesVersionDetailIssueList$key } from '@atlassian/jira-relay/src/__generated__/emptyView_softwareReleasesVersionDetailIssueList.graphql';
import { AddIssuesToVersion } from '@atlassian/jira-software-releases-add-issues-to-version/src/ui/async.tsx';
import { useFilterSortingQueryParams } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/index.tsx';
import noIssuesInVersionImageUrl from './assets/noIssuesInVersion.svg';
import noSearchResultsImageUrl from './assets/noSearchResults.svg';
import messages from './messages.tsx';

type Props = {
	jiraVersion: emptyView_softwareReleasesVersionDetailIssueList$key;
	isAnyFilterApplied: boolean;
	textFilter: string | null;
};

export const EmptyView = ({
	jiraVersion,
	isAnyFilterApplied,
	textFilter,
}: Props): React.JSX.Element => {
	const { clearAll } = useFilterSortingQueryParams();

	const { formatMessage } = useIntl();

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment emptyView_softwareReleasesVersionDetailIssueList on JiraVersion {
				canAddAndRemoveIssues @required(action: LOG)
				...ui_softwareReleasesAddIssuesToVersion_AddIssuesToVersion
			}
		`,
		jiraVersion,
	);

	return (
		<Container data-testid="software-releases-version-detail-issue-list.ui.empty-view">
			{isAnyFilterApplied ? (
				<EmptyState
					headingLevel={2}
					header={
						textFilter !== null
							? formatMessage(messages.noIssuesMatchTextFilterHeader, { textFilter })
							: formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.noIssuesMatchFilterHeaderIssueTerminologyRefreshM4
										: messages.noIssuesMatchFilterHeader,
								)
					}
					description={
						textFilter !== null
							? formatMessage(messages.noIssuesMatchTextFilterDescription)
							: formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.noIssuesMatchFilterDescriptionIssueTerminologyRefreshM4
										: messages.noIssuesMatchFilterDescription,
								)
					}
					imageUrl={noSearchResultsImageUrl}
					imageWidth={64}
					imageHeight={92}
					primaryAction={
						<Button
							appearance="primary"
							onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
								clearAll();
								fireUIAnalytics(analyticsEvent, 'clearFilter');
							}}
						>
							{formatMessage(messages.clearFilters)}
						</Button>
					}
				/>
			) : (
				<EmptyState
					headingLevel={2}
					header={formatMessage(messages.noIssuesInVersionHeader)}
					description={formatMessage(
						fg('jira-issue-terminology-update-m4')
							? messages.noIssuesInVersionDescriptionIssueTerminologyRefreshM4
							: messages.noIssuesInVersionDescription,
					)}
					imageUrl={noIssuesInVersionImageUrl}
					imageWidth={128}
					imageHeight={104}
					primaryAction={
						version?.canAddAndRemoveIssues === true ? (
							<AddIssuesToVersion appearance="primary" jiraVersion={version} />
						) : undefined
					}
				/>
			)}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	paddingTop: token('space.250'),
});
